import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/components/ui/use-toast';
import {
  Plus,
  X,
  Info,
  AlertTriangle,
  CheckCircle,
  Settings,
  Globe,
  Monitor,
  Ruler,
  DollarSign,
  FileText,
  Image,
  Link,
  Mail,
  Phone,
} from 'lucide-react';
import { getMeasurementUnits, MeasurementUnitsResponse } from '@/services/importService';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useAttributeTranslations } from '@/hooks/useAttributeTranslations';
import { cn } from '@/lib/utils';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { logError } from '@/utils/errorHandler';

interface AttributeFormData {
  code: string;
  label: string;
  data_type: string;
  is_localisable: boolean;
  is_scopable: boolean;
  is_required: boolean;
  validation_rule: string;
  allowed_units: string[];
  default_unit: string;
  default_currency: string;
  allowed_html_tags: string[];
  allowed_file_types: string[];
  max_file_size: number;
  allow_negative_numbers: boolean;
  options: Array<{ value: string; label: string; order: number }>;
}

interface EnhancedAttributeModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AttributeFormData) => Promise<void>;
  initialData?: Partial<AttributeFormData>;
  mode: 'create' | 'edit';
  isPending?: boolean;
}

const DEFAULT_HTML_TAGS = [
  'p',
  'ul',
  'li',
  'ol',
  'strong',
  'em',
  'br',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
];
const DEFAULT_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'];

export function EnhancedAttributeModal({
  isOpen,
  onOpenChange,
  onSubmit,
  initialData,
  mode,
  isPending = false,
}: EnhancedAttributeModalProps) {
  // Form state
  const [formData, setFormData] = useState<AttributeFormData>({
    code: '',
    label: '',
    data_type: 'text',
    is_localisable: false,
    is_scopable: false,
    is_required: false,
    validation_rule: '',
    allowed_units: [],
    default_unit: '',
    default_currency: '',
    allowed_html_tags: DEFAULT_HTML_TAGS,
    allowed_file_types: DEFAULT_FILE_TYPES,
    max_file_size: 10485760, // 10MB
    allow_negative_numbers: true,
    options: [],
  });

  // UI state
  const [activeTab, setActiveTab] = useState('basic');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [measurementUnits, setMeasurementUnits] = useState<MeasurementUnitsResponse | null>(null);
  const [newUnit, setNewUnit] = useState('');
  const [newHtmlTag, setNewHtmlTag] = useState('');
  const [newFileType, setNewFileType] = useState('');
  const [newOptionValue, setNewOptionValue] = useState('');
  const [newOptionLabel, setNewOptionLabel] = useState('');

  // Validation preview state
  const [testValue, setTestValue] = useState('');
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    error?: string;
    validated_value?: any;
    warnings?: string[];
  } | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const { toast } = useToast();
  const { currencies } = useOrgSettings();
  const {
    getModalText,
    getFieldText,
    getDataTypeText,
    getValidationText,
    getMeasurementText,
    getPriceText,
    getRichTextText,
    getMediaText,
    getButtonText,
    getTestValidationText,
    getToastMessage,
  } = useAttributeTranslations();

  // Load measurement units on mount
  useEffect(() => {
    if (isOpen) {
      loadMeasurementUnits();
    }
  }, [isOpen]);

  // Initialize form data when modal opens or initialData changes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData(prev => ({ ...prev, ...initialData }));
      } else {
        // Reset to defaults for create mode
        setFormData({
          code: '',
          label: '',
          data_type: 'text',
          is_localisable: false,
          is_scopable: false,
          is_required: false,
          validation_rule: '',
          allowed_units: [],
          default_unit: '',
          default_currency: '',
          allowed_html_tags: DEFAULT_HTML_TAGS,
          allowed_file_types: DEFAULT_FILE_TYPES,
          max_file_size: 10485760,
          allow_negative_numbers: true,
          options: [],
        });
      }
      setErrors({});
      setActiveTab('basic');
    }
  }, [isOpen, initialData]);

  const loadMeasurementUnits = async () => {
    try {
      const response = await getMeasurementUnits();
      setMeasurementUnits(response.data);
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: 'EnhancedAttributeModal',
        action: 'loadMeasurementUnits',
        metadata: { context: 'measurement_units_loading' },
      });
    }
  };

  // Handle form field changes
  const handleFieldChange = (field: keyof AttributeFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = getValidationText('codeRequired');
    } else if (!/^[a-z0-9_]+$/.test(formData.code)) {
      newErrors.code = getValidationText('codeInvalid');
    }

    if (!formData.label.trim()) {
      newErrors.label = getValidationText('labelRequired');
    }

    // Measurement-specific validation
    if (formData.data_type === 'measurement') {
      if (formData.default_unit && !formData.allowed_units.includes(formData.default_unit)) {
        newErrors.default_unit = getValidationText('defaultUnitInvalid');
      }
    }

    // Price-specific validation
    if (formData.data_type === 'price') {
      if (
        formData.default_currency &&
        !currencies.some(c => c.iso_code === formData.default_currency)
      ) {
        newErrors.default_currency = getValidationText('invalidCurrency');
      }
    }

    if (!formData.data_type) {
      newErrors.data_type = getValidationText('dataTypeRequired');
    }

    // Select-specific validation
    if (formData.data_type === 'select' && formData.options.length === 0) {
      newErrors.options = 'Select attributes must have at least one option configured';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      const toastData = getToastMessage('modal', 'validationError', '', {});
      toast({
        title: toastData.title,
        description: toastData.description,
        variant: 'destructive',
      });
      return;
    }

    try {
      await onSubmit(formData);
      onOpenChange(false);

      const toastKey = mode === 'create' ? 'createSuccess' : 'updateSuccess';
      const toastData = getToastMessage('modal', toastKey, '', {
        label: formData.label,
      });
      toast({
        title: toastData.title,
        description: toastData.description,
      });
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: 'EnhancedAttributeModal',
        action: 'handleSubmit',
        metadata: {
          mode: mode,
          formData: formData,
          context: 'form_submission',
        },
      });
      const toastData = getToastMessage('modal', 'submissionError', '', {});
      toast({
        title: toastData.title,
        description: toastData.description,
        variant: 'destructive',
      });
    }
  };

  // Add unit to allowed units
  const addUnit = () => {
    if (newUnit.trim() && !formData.allowed_units.includes(newUnit.trim())) {
      const updatedUnits = [...formData.allowed_units, newUnit.trim()];
      handleFieldChange('allowed_units', updatedUnits);

      // Auto-set as default if it's the first/only unit
      if (updatedUnits.length === 1) {
        handleFieldChange('default_unit', newUnit.trim());
      }

      setNewUnit('');
    }
  };

  // Remove unit from allowed units
  const removeUnit = (unit: string) => {
    const updatedUnits = formData.allowed_units.filter(u => u !== unit);
    handleFieldChange('allowed_units', updatedUnits);

    // Clear default unit if it was the removed unit
    if (formData.default_unit === unit) {
      // Auto-set to the remaining unit if there's only one left
      if (updatedUnits.length === 1) {
        handleFieldChange('default_unit', updatedUnits[0]);
      } else {
        handleFieldChange('default_unit', '');
      }
    }
  };

  // Add HTML tag
  const addHtmlTag = () => {
    if (newHtmlTag.trim() && !formData.allowed_html_tags.includes(newHtmlTag.trim())) {
      handleFieldChange('allowed_html_tags', [...formData.allowed_html_tags, newHtmlTag.trim()]);
      setNewHtmlTag('');
    }
  };

  // Remove HTML tag
  const removeHtmlTag = (tag: string) => {
    handleFieldChange(
      'allowed_html_tags',
      formData.allowed_html_tags.filter(t => t !== tag)
    );
  };

  // Add file type
  const addFileType = () => {
    if (newFileType.trim() && !formData.allowed_file_types.includes(newFileType.trim())) {
      handleFieldChange('allowed_file_types', [...formData.allowed_file_types, newFileType.trim()]);
      setNewFileType('');
    }
  };

  // Remove file type
  const removeFileType = (type: string) => {
    handleFieldChange(
      'allowed_file_types',
      formData.allowed_file_types.filter(t => t !== type)
    );
  };

  // Select option management functions
  const addOption = () => {
    // For select attributes we only need the label; use the same label as the stored value.
    const isSelect = formData.data_type === 'select';

    const labelOnly = newOptionLabel.trim();
    const rawValue = isSelect ? labelOnly : newOptionValue.trim();

    if (!rawValue) return;

    const value = rawValue; // stored value sent to the backend
    const label = labelOnly || value;

    // Check for duplicate values
    if (formData.options.some(opt => opt.value === value)) {
      toast({
        title: 'Duplicate Value',
        description: 'An option with this value already exists.',
        variant: 'destructive',
      });
      return;
    }

    const newOption = {
      value,
      label,
      order: formData.options.length,
    };

    handleFieldChange('options', [...formData.options, newOption]);
    setNewOptionLabel('');
    if (!isSelect) setNewOptionValue('');
  };

  const removeOption = (index: number) => {
    const updatedOptions = formData.options.filter((_, i) => i !== index);
    // Reorder remaining options
    const reorderedOptions = updatedOptions.map((opt, i) => ({
      ...opt,
      order: i,
    }));
    handleFieldChange('options', reorderedOptions);
  };

  const moveOption = (fromIndex: number, toIndex: number) => {
    const newOptions = [...formData.options];
    const [movedOption] = newOptions.splice(fromIndex, 1);
    newOptions.splice(toIndex, 0, movedOption);
    handleFieldChange('options', newOptions);
  };

  // Validation preview function
  const testValidation = async () => {
    if (!testValue.trim()) {
      setValidationResult(null);
      return;
    }

    setIsValidating(true);
    try {
      // Create a temporary attribute object for validation
      const tempAttribute = {
        id: 'temp',
        code: formData.code || 'test_attribute',
        data_type: formData.data_type,
        validation_rule: formData.validation_rule,
        allowed_units: formData.allowed_units,
        default_unit: formData.default_unit,
        default_currency: formData.default_currency,
        is_required: formData.is_required,
        allow_negative_numbers: formData.allow_negative_numbers,
        allowed_html_tags: formData.allowed_html_tags,
        allowed_file_types: formData.allowed_file_types,
        max_file_size: formData.max_file_size,
        options: formData.options,
      };

      // For demonstration purposes, simulate validation
      // In a real implementation, you might call a preview API
      const result = await simulateValidation(tempAttribute, testValue);
      setValidationResult(result);
    } catch (error) {
      setValidationResult({
        valid: false,
        error: error instanceof Error ? error.message : 'Validation failed',
      });
    } finally {
      setIsValidating(false);
    }
  };

  // Simulate validation for preview (replace with actual API call if needed)
  const simulateValidation = async (attribute: any, value: string) => {
    // Basic validation simulation based on data type
    const warnings: string[] = [];

    switch (attribute.data_type) {
      case 'text':
        if (attribute.validation_rule) {
          try {
            const regex = new RegExp(attribute.validation_rule);
            if (!regex.test(value)) {
              return {
                valid: false,
                error: `Text doesn't match pattern: ${attribute.validation_rule}`,
              };
            }
          } catch (e) {
            return {
              valid: false,
              error: `Invalid regex pattern: ${attribute.validation_rule}`,
            };
          }
        }
        return { valid: true, validated_value: value.trim(), warnings };

      case 'number':
        const num = parseFloat(value);
        if (isNaN(num)) {
          return { valid: false, error: 'Invalid number format' };
        }
        if (!attribute.allow_negative_numbers && num < 0) {
          return { valid: false, error: 'Negative numbers are not allowed' };
        }
        return { valid: true, validated_value: num, warnings };

      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return { valid: false, error: 'Invalid email format' };
        }
        return {
          valid: true,
          validated_value: value.toLowerCase().trim(),
          warnings,
        };

      case 'url':
        try {
          let url = value.trim();
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = 'https://' + url;
            warnings.push('Added HTTPS protocol');
          }
          new URL(url);
          return { valid: true, validated_value: url, warnings };
        } catch {
          return { valid: false, error: 'Invalid URL format' };
        }

      case 'phone':
        const phoneRegex = /^\+?[0-9\-\s]{7,}$/;
        if (!phoneRegex.test(value)) {
          return { valid: false, error: 'Invalid phone number format' };
        }
        return { valid: true, validated_value: value.trim(), warnings };

      case 'measurement':
        try {
          const parts = value.trim().split(/\s+/);
          if (parts.length === 1) {
            const amount = parseFloat(parts[0]);
            if (isNaN(amount)) {
              return { valid: false, error: 'Invalid measurement amount' };
            }
            if (attribute.default_unit) {
              warnings.push(`Using default unit: ${attribute.default_unit}`);
              return {
                valid: true,
                validated_value: { amount, unit: attribute.default_unit },
                warnings,
              };
            } else {
              return {
                valid: false,
                error: 'No unit specified and no default unit configured',
              };
            }
          } else if (parts.length === 2) {
            const amount = parseFloat(parts[0]);
            const unit = parts[1];
            if (isNaN(amount)) {
              return { valid: false, error: 'Invalid measurement amount' };
            }
            if (attribute.allowed_units.length > 0 && !attribute.allowed_units.includes(unit)) {
              return {
                valid: false,
                error: `Unit '${unit}' not in allowed units: ${attribute.allowed_units.join(', ')}`,
              };
            }
            return { valid: true, validated_value: { amount, unit }, warnings };
          } else {
            return {
              valid: false,
              error: 'Invalid measurement format. Use "amount unit" format.',
            };
          }
        } catch {
          return { valid: false, error: 'Invalid measurement format' };
        }

      case 'price':
        try {
          const parts = value.trim().split(/\s+/);
          if (parts.length === 1) {
            const amount = parseFloat(parts[0]);
            if (isNaN(amount)) {
              return { valid: false, error: 'Invalid price amount' };
            }
            if (attribute.default_currency) {
              warnings.push(`Using default currency: ${attribute.default_currency}`);
              return {
                valid: true,
                validated_value: {
                  amount,
                  currency: attribute.default_currency,
                },
                warnings,
              };
            } else {
              return {
                valid: false,
                error: 'No currency specified and no default currency configured',
              };
            }
          } else if (parts.length === 2) {
            const amount = parseFloat(parts[0]);
            const currency = parts[1].toUpperCase();
            if (isNaN(amount)) {
              return { valid: false, error: 'Invalid price amount' };
            }
            return {
              valid: true,
              validated_value: { amount, currency },
              warnings,
            };
          } else {
            return {
              valid: false,
              error: 'Invalid price format. Use "amount currency" format.',
            };
          }
        } catch {
          return { valid: false, error: 'Invalid price format' };
        }

      case 'select':
        if (attribute.options.length === 0) {
          return {
            valid: false,
            error: 'No options configured for select attribute',
          };
        }
        const validOption = attribute.options.find((opt: any) => opt.value === value);
        if (!validOption) {
          const availableOptions = attribute.options.map((opt: any) => opt.value).join(', ');
          return {
            valid: false,
            error: `Invalid option. Valid options: ${availableOptions}`,
          };
        }
        return { valid: true, validated_value: value, warnings };

      case 'boolean':
        const lowerValue = value.toLowerCase().trim();
        if (['true', '1', 'yes', 'on', 'y'].includes(lowerValue)) {
          return { valid: true, validated_value: true, warnings };
        } else if (['false', '0', 'no', 'off', 'n'].includes(lowerValue)) {
          return { valid: true, validated_value: false, warnings };
        } else {
          return {
            valid: false,
            error: 'Invalid boolean value. Use: true/false, 1/0, yes/no',
          };
        }

      case 'date':
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return { valid: false, error: 'Invalid date format' };
          }
          return {
            valid: true,
            validated_value: date.toISOString().split('T')[0],
            warnings,
          };
        } catch {
          return { valid: false, error: 'Invalid date format' };
        }

      default:
        return { valid: true, validated_value: value, warnings };
    }
  };

  // Get example values for different attribute types
  const getExampleValue = (dataType: string): string => {
    switch (dataType) {
      case 'text':
        return 'Sample text value';
      case 'number':
        return '42.5';
      case 'email':
        return 'user@example.com';
      case 'url':
        return 'https://example.com';
      case 'phone':
        return '+1-555-123-4567';
      case 'measurement':
        return '25 cm';
      case 'price':
        return '19.99 USD';
      case 'boolean':
        return 'true';
      case 'date':
        return '2024-01-15';
      case 'select':
        return formData.options[0]?.value || 'option1';
      case 'rich_text':
        return '<p>Rich <strong>text</strong> content</p>';
      default:
        return 'sample value';
    }
  };

  // Get validation examples for testing
  const getValidationExamples = (
    dataType: string
  ): Array<{ value: string; valid: boolean; description?: string }> => {
    switch (dataType) {
      case 'text':
        return [
          { value: 'Valid text', valid: true, description: 'Normal text' },
          { value: '', valid: false, description: 'Empty value' },
          { value: '   ', valid: false, description: 'Whitespace only' },
        ];
      case 'number':
        return [
          { value: '42', valid: true, description: 'Integer' },
          { value: '3.14', valid: true, description: 'Decimal' },
          {
            value: '-5',
            valid: !formData.allow_negative_numbers,
            description: 'Negative number',
          },
          { value: 'abc', valid: false, description: 'Invalid number' },
        ];
      case 'email':
        return [
          {
            value: 'user@example.com',
            valid: true,
            description: 'Valid email',
          },
          {
            value: 'test.email+tag@domain.co.uk',
            valid: true,
            description: 'Complex email',
          },
          {
            value: 'invalid-email',
            valid: false,
            description: 'Missing @ and domain',
          },
          {
            value: '@example.com',
            valid: false,
            description: 'Missing username',
          },
        ];
      case 'url':
        return [
          {
            value: 'https://example.com',
            valid: true,
            description: 'Complete URL',
          },
          {
            value: 'example.com',
            valid: true,
            description: 'Domain only (HTTPS added)',
          },
          {
            value: 'ftp://files.example.com',
            valid: false,
            description: 'Non-HTTP protocol',
          },
          {
            value: 'not-a-url',
            valid: false,
            description: 'Invalid URL format',
          },
        ];
      case 'phone':
        return [
          {
            value: '+1-555-123-4567',
            valid: true,
            description: 'International format',
          },
          { value: '555-123-4567', valid: true, description: 'Local format' },
          { value: '1234567', valid: true, description: 'Digits only' },
          { value: '123', valid: false, description: 'Too short' },
        ];
      case 'measurement':
        return [
          { value: '25 cm', valid: true, description: 'Amount with unit' },
          {
            value: '10',
            valid: formData.default_unit ? true : false,
            description: 'Amount only (needs default unit)',
          },
          { value: '5 invalid', valid: false, description: 'Invalid unit' },
          { value: 'abc cm', valid: false, description: 'Invalid amount' },
        ];
      case 'price':
        return [
          {
            value: '19.99 USD',
            valid: true,
            description: 'Amount with currency',
          },
          {
            value: '25',
            valid: formData.default_currency ? true : false,
            description: 'Amount only (needs default currency)',
          },
          { value: '10.50 XXX', valid: false, description: 'Invalid currency' },
          { value: 'abc USD', valid: false, description: 'Invalid amount' },
        ];
      case 'boolean':
        return [
          { value: 'true', valid: true, description: 'Boolean true' },
          { value: 'false', valid: true, description: 'Boolean false' },
          { value: '1', valid: true, description: 'Numeric true' },
          { value: '0', valid: true, description: 'Numeric false' },
          { value: 'maybe', valid: false, description: 'Invalid boolean' },
        ];
      case 'date':
        return [
          { value: '2024-01-15', valid: true, description: 'ISO date format' },
          {
            value: '2024/01/15',
            valid: true,
            description: 'Alternative format',
          },
          {
            value: 'January 15, 2024',
            valid: true,
            description: 'Long format',
          },
          { value: 'invalid-date', valid: false, description: 'Invalid date' },
        ];
      case 'select':
        const options =
          formData.options.length > 0
            ? formData.options
            : [{ value: 'option1', label: 'Option 1' }];
        return [
          {
            value: options[0]?.value || 'option1',
            valid: true,
            description: 'Valid option',
          },
          {
            value: 'invalid-option',
            valid: false,
            description: 'Invalid option',
          },
          ...(options.length > 1
            ? [
                {
                  value: options[1].value,
                  valid: true,
                  description: 'Another valid option',
                },
              ]
            : []),
        ];
      default:
        return [{ value: 'sample value', valid: true, description: 'Sample input' }];
    }
  };

  // Get type icon
  const getTypeIcon = (type: string) => {
    const typeConfig = ATTRIBUTE_TYPES.find(t => t.value === type);
    return typeConfig?.icon || Settings;
  };

  // Get type description
  const getTypeDescription = (type: string) => {
    const descriptions = {
      text: 'Simple text values with optional validation rules',
      number: 'Numeric values (integers or decimals) with optional range validation',
      boolean: 'True/false toggle values',
      date: 'Date and time values with automatic parsing',
      select: 'Single choice from a predefined list of options (dropdown)',
      rich_text: 'HTML-formatted text content with configurable allowed tags',
      price: 'Monetary values with currency support',
      media: 'File attachments (images, videos, documents)',
      measurement: 'Numeric values with unit specifications (length, weight, etc.)',
      url: 'Website URLs with automatic validation',
      email: 'Email addresses with format validation',
      phone: 'Phone numbers with international format support',
    };
    return descriptions[type as keyof typeof descriptions] || 'Unknown attribute type';
  };

  // Generate attribute types with translated labels and descriptions
  const ATTRIBUTE_TYPES = [
    {
      value: 'text',
      label: getDataTypeText('text'),
      icon: FileText,
      description: getDataTypeText('text', 'description'),
    },
    {
      value: 'number',
      label: getDataTypeText('number'),
      icon: Settings,
      description: getDataTypeText('number', 'description'),
    },
    {
      value: 'boolean',
      label: getDataTypeText('boolean'),
      icon: CheckCircle,
      description: getDataTypeText('boolean', 'description'),
    },
    {
      value: 'date',
      label: getDataTypeText('date'),
      icon: Settings,
      description: getDataTypeText('date', 'description'),
    },
    {
      value: 'select',
      label: getDataTypeText('select'),
      icon: Settings,
      description: getDataTypeText('select', 'description'),
    },
    {
      value: 'rich_text',
      label: getDataTypeText('rich_text'),
      icon: FileText,
      description: getDataTypeText('rich_text', 'description'),
    },
    {
      value: 'price',
      label: getDataTypeText('price'),
      icon: DollarSign,
      description: getDataTypeText('price', 'description'),
    },
    {
      value: 'media',
      label: getDataTypeText('media'),
      icon: Image,
      description: getDataTypeText('media', 'description'),
    },
    {
      value: 'measurement',
      label: getDataTypeText('measurement'),
      icon: Ruler,
      description: getDataTypeText('measurement', 'description'),
    },
    {
      value: 'url',
      label: getDataTypeText('url'),
      icon: Link,
      description: getDataTypeText('url', 'description'),
    },
    {
      value: 'email',
      label: getDataTypeText('email'),
      icon: Mail,
      description: getDataTypeText('email', 'description'),
    },
    {
      value: 'phone',
      label: getDataTypeText('phone'),
      icon: Phone,
      description: getDataTypeText('phone', 'description'),
    },
  ];

  // Render basic configuration tab
  const renderBasicTab = () => (
    <div className="space-y-6 px-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="code">
            {getFieldText('codeLabel')}{' '}
            <span className="text-red-500">{getFieldText('codeRequired')}</span>
          </Label>
          <Input
            id="code"
            value={formData.code}
            onChange={e => handleFieldChange('code', e.target.value)}
            placeholder={getFieldText('codePlaceholder')}
            disabled={mode === 'edit'} // Code cannot be changed after creation
          />
          {errors.code && <p className="text-sm text-red-500">{errors.code}</p>}
          <p className="text-xs text-muted-foreground">{getFieldText('codeHelp')}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="label">
            {getFieldText('labelLabel')}{' '}
            <span className="text-red-500">{getFieldText('labelRequired')}</span>
          </Label>
          <Input
            id="label"
            value={formData.label}
            onChange={e => handleFieldChange('label', e.target.value)}
            placeholder={getFieldText('labelPlaceholder')}
          />
          {errors.label && <p className="text-sm text-red-500">{errors.label}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label>{getFieldText('dataTypeLabel')}</Label>
        <Select
          value={formData.data_type}
          onValueChange={value => handleFieldChange('data_type', value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ATTRIBUTE_TYPES.map(type => {
              const IconComponent = type.icon;
              return (
                <SelectItem key={type.value} value={type.value}>
                  <div className="flex items-center space-x-2">
                    <IconComponent className="size-4" />
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-muted-foreground">{type.description}</div>
                    </div>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="flex flex-col space-y-2">
          <ToggleSwitch
            checked={formData.is_localisable}
            onCheckedChange={checked => handleFieldChange('is_localisable', checked)}
            label={getFieldText('localizableLabel')}
          />
          <p className="text-xs text-muted-foreground">{getFieldText('localizableHelp')}</p>
        </div>

        <div className="flex flex-col space-y-2">
          <ToggleSwitch
            checked={formData.is_scopable}
            onCheckedChange={checked => handleFieldChange('is_scopable', checked)}
            label={getFieldText('scopableLabel')}
          />
          <p className="text-xs text-muted-foreground">{getFieldText('scopableHelp')}</p>
        </div>

        <div className="flex flex-col space-y-2">
          <ToggleSwitch
            checked={formData.is_required}
            onCheckedChange={checked => handleFieldChange('is_required', checked)}
            label={getFieldText('requiredLabel')}
          />
          <p className="text-xs text-muted-foreground">{getFieldText('requiredHelp')}</p>
        </div>
      </div>
    </div>
  );

  // Render validation tab
  const renderValidationTab = () => (
    <div className="space-y-6">
      {/* General validation rule */}
      {['text', 'number', 'url', 'email'].includes(formData.data_type) && (
        <div className="space-y-2">
          <Label htmlFor="validation_rule">{getFieldText('validationRuleLabel')}</Label>
          <Textarea
            id="validation_rule"
            value={formData.validation_rule}
            onChange={e => handleFieldChange('validation_rule', e.target.value)}
            placeholder={getFieldText('validationRulePlaceholder')}
            rows={3}
          />
          <p className="text-xs text-muted-foreground">{getFieldText('validationRuleHelp')}</p>
        </div>
      )}

      {/* Number-specific validation */}
      {['number', 'measurement'].includes(formData.data_type) && (
        <div className="flex items-center space-x-2">
          <ToggleSwitch
            checked={formData.allow_negative_numbers}
            onCheckedChange={checked => handleFieldChange('allow_negative_numbers', checked)}
            label={getFieldText('allowNegativeNumbersLabel')}
          />
        </div>
      )}

      {/* Select-specific configuration */}
      {formData.data_type === 'select' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-base">
              <Settings className="size-4" />
              <span>Select Options</span>
            </CardTitle>
            <CardDescription>
              Configure the available options for this select attribute
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {formData.data_type === 'select' ? (
                <div className="space-y-1">
                  <Label className="text-xs">Label</Label>
                  <Input
                    value={newOptionLabel}
                    onChange={e => setNewOptionLabel(e.target.value)}
                    placeholder="Red"
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addOption();
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Value (stored)</Label>
                    <Input
                      value={newOptionValue}
                      onChange={e => setNewOptionValue(e.target.value)}
                      placeholder="red"
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addOption();
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Label (displayed)</Label>
                    <Input
                      value={newOptionLabel}
                      onChange={e => setNewOptionLabel(e.target.value)}
                      placeholder="Red"
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addOption();
                        }
                      }}
                    />
                  </div>
                </div>
              )}
              <Button
                onClick={addOption}
                variant="outline"
                size="sm"
                className="w-full"
                disabled={
                  formData.data_type === 'select' ? !newOptionLabel.trim() : !newOptionValue.trim()
                }
              >
                <Plus className="mr-2 size-4" />
                Add Option
              </Button>
            </div>

            {formData.options.length > 0 && (
              <div className="space-y-2">
                <Label>Options ({formData.options.length})</Label>
                <div className="max-h-60 space-y-2 overflow-y-auto">
                  {formData.options.map((option, index) => (
                    <div
                      key={option.value}
                      className="flex items-center justify-between rounded-lg border bg-gray-50 p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2">
                          {formData.data_type !== 'select' && (
                            <Badge variant="secondary" className="font-mono text-xs">
                              {option.value}
                            </Badge>
                          )}
                          <span className="font-medium">{option.label}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {index > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveOption(index, index - 1)}
                            className="size-8 p-0"
                          >
                            ↑
                          </Button>
                        )}
                        {index < formData.options.length - 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveOption(index, index + 1)}
                            className="size-8 p-0"
                          >
                            ↓
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOption(index)}
                          className="size-8 p-0 hover:bg-red-100"
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {formData.options.length === 0 && (
                  <div className="py-8 text-center text-muted-foreground">
                    <Settings className="mx-auto mb-2 size-8 opacity-50" />
                    <p>No options configured</p>
                    <p className="text-xs">Add options above to get started</p>
                  </div>
                )}
              </div>
            )}

            {formData.options.length === 0 && (
              <Alert>
                <AlertTriangle className="size-4" />
                <AlertDescription>
                  Select attributes require at least one option to be functional. Users won't be
                  able to set values until options are configured.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Measurement-specific configuration */}
      {formData.data_type === 'measurement' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-base">
              <Ruler className="size-4" />
              <span>{getMeasurementText('title')}</span>
            </CardTitle>
            <CardDescription>{getMeasurementText('description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{getMeasurementText('allowedUnitsLabel')}</Label>
              <div className="flex space-x-2">
                <Input
                  value={newUnit}
                  onChange={e => setNewUnit(e.target.value)}
                  placeholder={getMeasurementText('addUnitPlaceholder')}
                  onKeyDown={e => e.key === 'Enter' && addUnit()}
                />
                <Button onClick={addUnit} variant="outline" size="sm">
                  <Plus className="size-4" />
                </Button>
              </div>

              {measurementUnits && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {getMeasurementText('commonUnitsLabel')}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(measurementUnits.units_by_category).map(([category, units]) => (
                      <div key={category} className="space-y-1">
                        <p className="text-xs font-medium capitalize">{category}:</p>
                        <div className="flex flex-wrap gap-1">
                          {units.map(unit => (
                            <Button
                              key={unit}
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => {
                                if (!formData.allowed_units.includes(unit)) {
                                  const updatedUnits = [...formData.allowed_units, unit];
                                  handleFieldChange('allowed_units', updatedUnits);

                                  // Auto-set as default if it's the first/only unit
                                  if (updatedUnits.length === 1) {
                                    handleFieldChange('default_unit', unit);
                                  }
                                }
                              }}
                            >
                              {unit}
                            </Button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {formData.allowed_units.map(unit => {
                  const isDefault = formData.default_unit === unit;
                  return (
                    <Badge
                      key={unit}
                      variant={isDefault ? 'default' : 'secondary'}
                      className={cn(
                        'flex items-center space-x-1',
                        isDefault && 'ring-2 ring-blue-500 bg-blue-100 text-blue-800'
                      )}
                    >
                      <span>{unit}</span>
                      {isDefault && <span className="text-xs font-bold">(default)</span>}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="size-4 p-0 hover:bg-red-100"
                        onClick={() => removeUnit(unit)}
                      >
                        <X className="size-3" />
                      </Button>
                    </Badge>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{getMeasurementText('defaultUnitLabel')}</Label>
              <Select
                value={formData.default_unit || 'none'}
                onValueChange={value =>
                  handleFieldChange('default_unit', value === 'none' ? '' : value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={getMeasurementText('selectDefaultUnit')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{getMeasurementText('noDefault')}</SelectItem>
                  {formData.allowed_units.map(unit => (
                    <SelectItem key={unit} value={unit}>
                      {unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.default_unit && <p className="text-sm text-red-500">{errors.default_unit}</p>}
              <p className="text-xs text-muted-foreground">
                {getMeasurementText('defaultUnitHelp')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Price-specific configuration */}
      {formData.data_type === 'price' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-base">
              <DollarSign className="size-4" />
              <span>{getPriceText('title')}</span>
            </CardTitle>
            <CardDescription>{getPriceText('description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>{getPriceText('defaultCurrencyLabel')}</Label>
              <Select
                value={formData.default_currency || 'none'}
                onValueChange={value =>
                  handleFieldChange('default_currency', value === 'none' ? '' : value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={getPriceText('selectDefaultCurrency')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{getPriceText('noDefault')}</SelectItem>
                  {currencies.map(currency => (
                    <SelectItem key={currency.iso_code} value={currency.iso_code}>
                      {currency.iso_code} - {currency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.default_currency && (
                <p className="text-sm text-red-500">{errors.default_currency}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rich text configuration */}
      {formData.data_type === 'rich_text' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{getRichTextText('title')}</CardTitle>
            <CardDescription>{getRichTextText('description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{getRichTextText('allowedTagsLabel')}</Label>
              <div className="flex space-x-2">
                <Input
                  value={newHtmlTag}
                  onChange={e => setNewHtmlTag(e.target.value)}
                  placeholder={getRichTextText('addTagPlaceholder')}
                  onKeyDown={e => e.key === 'Enter' && addHtmlTag()}
                />
                <Button onClick={addHtmlTag} variant="outline" size="sm">
                  <Plus className="size-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.allowed_html_tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="flex items-center space-x-1">
                    <span>{tag}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="size-4 p-0"
                      onClick={() => removeHtmlTag(tag)}
                    >
                      <X className="size-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Media configuration */}
      {formData.data_type === 'media' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-base">
              <Image className="size-4" />
              <span>{getMediaText('title')}</span>
            </CardTitle>
            <CardDescription>{getMediaText('description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{getMediaText('allowedTypesLabel')}</Label>
              <div className="flex space-x-2">
                <Input
                  value={newFileType}
                  onChange={e => setNewFileType(e.target.value)}
                  placeholder={getMediaText('addTypePlaceholder')}
                  onKeyDown={e => e.key === 'Enter' && addFileType()}
                />
                <Button onClick={addFileType} variant="outline" size="sm">
                  <Plus className="size-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.allowed_file_types.map(type => (
                  <Badge key={type} variant="secondary" className="flex items-center space-x-1">
                    <span>{type}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="size-4 p-0"
                      onClick={() => removeFileType(type)}
                    >
                      <X className="size-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{getMediaText('maxFileSizeLabel')}</Label>
              <Input
                type="number"
                value={formData.max_file_size}
                onChange={e => handleFieldChange('max_file_size', parseInt(e.target.value) || 0)}
              />
              <p className="text-xs text-muted-foreground">
                {getMediaText('currentSizeLabel', {
                  size: (formData.max_file_size / (1024 * 1024)).toFixed(1),
                })}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Render validation preview tab
  const renderValidationPreviewTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-base">
            <Settings className="size-4" />
            <span>{getTestValidationText('title')}</span>
          </CardTitle>
          <CardDescription>{getTestValidationText('description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="testValue">{getTestValidationText('testValueLabel')}</Label>
            <Input
              id="testValue"
              value={testValue}
              onChange={e => setTestValue(e.target.value)}
              placeholder={getTestValidationText('testValuePlaceholder', {
                example: getExampleValue(formData.data_type),
              })}
            />
            <p className="text-xs text-muted-foreground">
              {getTestValidationText('testValueHelp')}
            </p>
          </div>

          <div className="flex space-x-2">
            <Button
              onClick={testValidation}
              disabled={isValidating || !testValue.trim()}
              className="flex items-center space-x-2"
            >
              {isValidating ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>{getTestValidationText('testingButton')}</span>
                </>
              ) : (
                <>
                  <CheckCircle className="size-4" />
                  <span>{getTestValidationText('testButton')}</span>
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={() => setTestValue(getExampleValue(formData.data_type))}
              disabled={isValidating}
            >
              {getTestValidationText('useExampleButton')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {validationResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-base">
              {validationResult.valid ? (
                <CheckCircle className="size-4 text-green-500" />
              ) : (
                <AlertTriangle className="size-4 text-red-500" />
              )}
              <span>{getTestValidationText('validationResultTitle')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Badge variant={validationResult.valid ? 'default' : 'destructive'}>
                  {validationResult.valid
                    ? getTestValidationText('validStatus')
                    : getTestValidationText('invalidStatus')}
                </Badge>
                {validationResult.valid && (
                  <span className="text-sm text-green-600">
                    {getTestValidationText('validMessage')}
                  </span>
                )}
              </div>

              {!validationResult.valid && validationResult.error && (
                <Alert>
                  <AlertTriangle className="size-4" />
                  <AlertDescription className="text-red-600">
                    <strong>{getTestValidationText('errorPrefix')}</strong> {validationResult.error}
                  </AlertDescription>
                </Alert>
              )}

              {validationResult.valid && validationResult.validated_value !== undefined && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {getTestValidationText('processedValueLabel')}
                  </Label>
                  <div className="rounded bg-gray-50 p-2 font-mono text-sm">
                    {typeof validationResult.validated_value === 'object'
                      ? JSON.stringify(validationResult.validated_value, null, 2)
                      : String(validationResult.validated_value)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {getTestValidationText('processedValueHelp')}
                  </p>
                </div>
              )}

              {validationResult.warnings && validationResult.warnings.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {getTestValidationText('warningsLabel')}
                  </Label>
                  <div className="space-y-1">
                    {validationResult.warnings.map((warning, index) => (
                      <Alert key={index}>
                        <Info className="size-4" />
                        <AlertDescription className="text-amber-600">{warning}</AlertDescription>
                      </Alert>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Validation examples */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {getTestValidationText('validationExamplesTitle')}
          </CardTitle>
          <CardDescription>
            {getTestValidationText('validationExamplesDescription', {
              type: getDataTypeText(formData.data_type),
            })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {getValidationExamples(formData.data_type).map(
              (
                example: {
                  value: string;
                  valid: boolean;
                  description?: string;
                },
                index: number
              ) => (
                <div key={index} className="flex items-center justify-between rounded border p-2">
                  <span className="font-mono text-sm">{example.value}</span>
                  <div className="flex items-center space-x-2">
                    <Badge variant={example.valid ? 'default' : 'secondary'}>
                      {example.valid
                        ? getTestValidationText('validLabel')
                        : getTestValidationText('invalidLabel')}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => setTestValue(example.value)}>
                      {getTestValidationText('testExampleButton')}
                    </Button>
                  </div>
                </div>
              )
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? getModalText('createTitle') : getModalText('editTitle')}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? getModalText('createDescription')
              : getModalText('editDescription')}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">{getModalText('tabs.basic')}</TabsTrigger>
            <TabsTrigger value="validation">{getModalText('tabs.validation')}</TabsTrigger>
            <TabsTrigger value="preview">
              <Settings className="mr-1 size-4" />
              {getModalText('tabs.testValidation')}
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="mt-4 h-96">
            <TabsContent value="basic" className="space-y-6">
              {renderBasicTab()}
            </TabsContent>

            <TabsContent value="validation" className="space-y-6">
              {renderValidationTab()}
            </TabsContent>

            <TabsContent value="preview" className="space-y-6">
              {renderValidationPreviewTab()}
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {getButtonText('cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending
              ? getButtonText('saving')
              : mode === 'create'
                ? getButtonText('createAttribute')
                : getButtonText('updateAttribute')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
